import {getYear} from 'date-fns';
import {OptionsObject, SnackbarKey, SnackbarMessage} from 'notistack';
import {NavigateFunction} from 'react-router-dom';
import {
  ApolloCache,
  ApolloQueryResult,
  DefaultContext,
  MutationFunctionOptions,
} from '@apollo/client';
import {GetReviews} from '../../schema-types/GetReviews';
import {CreateReview, CreateReviewVariables} from '../../schema-types/CreateReview';
import {UpdateReview, UpdateReviewVariables} from '../../schema-types/UpdateReview';
import {SetInJourney, SetInJourneyVariables} from '../../schema-types/SetInJourney';
import {newUTCDate} from '../../utils/newUTCDate';
import {handoverStepRoutes} from '../../pages/HandoverSummaryPage/HandoverStepTypes';
import validateUUID from '../../utils/validateUUID';

export const reviewTypeLabelsToValues = {
  Annual: 'Ongoing client service',
  'Ad hoc': 'Change of client circumstances',
  Initial: 'Initial client meeting',
};
export const reviewTypeValuesToLabels = {
  'Ongoing client service': 'Annual',
  'Change of client circumstances': 'Ad hoc',
  'Initial client meeting': 'Initial',
};

export type reviewTypes =
  | 'Ongoing client service'
  | 'Initial client meeting'
  | 'Change of client circumstances';

export const onCreateReviewFactory = ({
  setLoading,
  reviewReason,
  enqueueSnackbar,
  navigate,
  accountId,
  createReview,
  refetchAccount,
  updateReview,
  previousReviewSelected,
  setIsJourneyToTrue,
}: {
  previousReviewSelected: string | null;
  setLoading: (value: React.SetStateAction<boolean>) => void;
  reviewReason: reviewTypes | undefined;
  refetchAccount: () => Promise<ApolloQueryResult<GetReviews>>;
  enqueueSnackbar: (
    message: SnackbarMessage,
    options?: OptionsObject | undefined
  ) => SnackbarKey;
  navigate: NavigateFunction;
  accountId: string | undefined;
  createReview: (
    options?:
      | MutationFunctionOptions<
          CreateReview,
          CreateReviewVariables,
          DefaultContext,
          ApolloCache<any>
        >
      | undefined
  ) => Promise<any>;
  updateReview: (
    options?:
      | MutationFunctionOptions<
          UpdateReview,
          UpdateReviewVariables,
          DefaultContext,
          ApolloCache<any>
        >
      | undefined
  ) => Promise<any>;
  setIsJourneyToTrue: (
    options?:
      | MutationFunctionOptions<
          SetInJourney,
          SetInJourneyVariables,
          DefaultContext,
          ApolloCache<any>
        >
      | undefined
  ) => Promise<any>;
}) => {
  return async (skipPrecursor: boolean, ignoreLimit: boolean) => {
    setLoading(true);
    const currentReviewsResponse = await refetchAccount();
    if (!currentReviewsResponse.data.accountDetails) {
      setLoading(false);
      return null;
    }
    const {data: accountReviewsData} = currentReviewsResponse;
    const {accountDetails} = accountReviewsData;
    if (!accountDetails) {
      setLoading(false);
      return null;
    }
    const skipParam = skipPrecursor ? `skip=${skipPrecursor ? 'true' : 'false'}` : '';
    // Tentative date for meeting with client
    // dateScheduled: "2022-08-03T23:00:00+00:00"
    // Confirmed date for meeting with client
    // dateBooked: "2022-08-23T23:00:00+00:00"

    // Checking if is annual review
    if (reviewReason === 'Ongoing client service') {
      const existingAnnualMeetingFutureBooking = accountDetails.draftReviews?.find(
        (dr) => {
          return dr.reviewReason === 'Ongoing client service';
        }
      );
      if (existingAnnualMeetingFutureBooking) {
        enqueueSnackbar(
          'You have been redirected to the existing active annual review.',
          {
            variant: 'warning',
            autoHideDuration: 5000,
          }
        );
        setLoading(false);
        navigate({
          pathname: `/account/${accountId}/review-journey/${existingAnnualMeetingFutureBooking.id}/${handoverStepRoutes.meeting}`,
        });
        return;
      }

      // then it's an annual review which needs creating
      let reviewId: string;
      return createReview({
        variables: {
          ignoreLimit,
          accountId: accountDetails.id!,
          reviewType: 'Annual',
          name: `Annual Review ${getYear(newUTCDate())}`,
        },
      })
        .then(async ({data, errors}) => {
          reviewId = data?.createReview?.review?.id;
          if (!reviewId) {
            setLoading(false);

            enqueueSnackbar(
              'Unable to create annual review, an annual review already exists',
              {
                variant: 'error',
                autoHideDuration: null,
              }
            );
            throw Error();
          }
          await setIsJourneyToTrue({
            variables: {
              reviewId: validateUUID(reviewId!)!,
              annualReview: true,
            },
          });
          navigate({
            pathname: `/account/${accountId}/review-journey/${reviewId}/header-precursor`,
            search: `${skipParam}`,
          });
        })
        .catch(() => {});
    }

    // ad-hoc review
    if (reviewReason === 'Change of client circumstances') {
      if (previousReviewSelected && previousReviewSelected !== 'new review') {
        enqueueSnackbar('Using existing ad hoc review', {
          variant: 'warning',
          autoHideDuration: 5000,
        });
        navigate({
          pathname: `/account/${accountId}/review-journey/${previousReviewSelected}/${handoverStepRoutes.meeting}`,
        });
        setLoading(false);
      } else {
        const adhocViable =
          accountDetails.name?.toLocaleLowerCase().includes('test') ||
          (accountDetails.draftReviews?.filter(
            (dr) => dr?.reviewReason === 'Change of client circumstances'
          )?.length || 0) < 3;
        let reviewId: string;
        if (adhocViable) {
          return createReview({
            variables: {
              ignoreLimit,
              accountId: accountDetails.id!,
              reviewType: 'Ad-hoc',
            },
          }).then(async ({data}) => {
            reviewId = data?.createReview?.review?.id;
            if (!reviewId) {
              setLoading(false);

              enqueueSnackbar('Unable to create this ad-hoc review', {
                variant: 'error',
                autoHideDuration: null,
              });
              throw Error();
            }
            await setIsJourneyToTrue({
              variables: {
                reviewId: validateUUID(reviewId!)!,
              },
            });
            navigate({
              pathname: `/account/${accountId}/review-journey/${reviewId}/header-precursor`,
              search: `${skipParam}`,
            });
            setLoading(false);
          });
        }
        enqueueSnackbar(
          'There are already 3 active adhoc reviews on this account, please select an existing one',
          {variant: 'error', autoHideDuration: null}
        );
        setLoading(false);
      }
    }

    if (reviewReason === 'Initial client meeting') {
      const existingInitialMeetingFutureBooking = accountDetails.draftReviews?.find(
        (dr) => {
          return dr.reviewReason === 'Initial client meeting';
        }
      );
      if (existingInitialMeetingFutureBooking) {
        enqueueSnackbar(
          'You have been redirected to the existing active initial review.',
          {
            variant: 'warning',
            autoHideDuration: 5000,
          }
        );
        setLoading(false);
        navigate({
          pathname: `/account/${accountId}/review-journey/${existingInitialMeetingFutureBooking.id}/${handoverStepRoutes.meeting}`,
        });
        return;
      }

      let reviewId: string;
      return createReview({
        variables: {
          ignoreLimit,
          accountId: accountReviewsData.accountDetails!.id!,
          reviewType: 'Initial',
        },
      })
        .then(async ({data}) => {
          reviewId = data?.createReview?.review?.id;
          if (!reviewId) {
            setLoading(false);

            enqueueSnackbar('Unable to create this initial review', {
              variant: 'error',
              autoHideDuration: null,
            });
            throw Error();
          }
          await setIsJourneyToTrue({
            variables: {
              reviewId: validateUUID(reviewId!)!,
            },
          });
          return updateReview({
            variables: {
              reviewId: validateUUID(reviewId!)!,
              reviewReason,
            },
          });
        })
        .then(({data}) => {
          setLoading(false);
          navigate({
            pathname: `/account/${accountId}/review-journey/${reviewId}/header-precursor`,
            search: `${skipParam}`,
          });
        });
    }
  };
};
